import React from 'react';
import {
  // Feature Icons
  ScanOutlined,
  LockOutlined,
  FilePdfOutlined,
  // Navigation Icons
  HomeOutlined,
  SendOutlined,
  MessageOutlined,
  SwapOutlined,
  UpOutlined,
  DownOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ReloadOutlined,
  MenuOutlined,
  UserOutlined,
  SettingOutlined,
  BarsOutlined,
  BookOutlined,
  PlusOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  CheckOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  EllipsisOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  AudioOutlined,
  FileOutlined,
  CalendarOutlined,
  CopyOutlined,
  BellOutlined,
  FormOutlined,
  QuestionCircleOutlined,
  GlobalOutlined,
  TagOutlined,
  FolderOutlined,
  HighlightOutlined,
  FileWordOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';

/**
 * Icon mapping for web - maps SF Symbol names to Ant Design Icons
 * Features section icons: Yazma/Tara, Güvenlik, PDF
 * Navigation & utility icons for the app
 */
const ICON_COMPONENTS = {
  // Feature Icons (Features Section)
  'write-speak-scan': ScanOutlined,
  'privacy-secure': LockOutlined,
  'pdf-export': FilePdfOutlined,

  // Navigation Icons
  'home': HomeOutlined,
  'paperplane.fill': SendOutlined,
  'message.bulleted': MessageOutlined,
  'arrow.up.arrow.down': SwapOutlined,
  'chevron.up': UpOutlined,
  'chevron.down': DownOutlined,
  'chevron.left': ArrowLeftOutlined,
  'chevron.right': ArrowRightOutlined,
  'chevron.forward': ArrowRightOutlined,
  'arrow.back': ArrowLeftOutlined,
  'arrow.forward': ArrowRightOutlined,
  'arrow.clockwise': ReloadOutlined,
  'menu.open': MenuOutlined,
  'person': UserOutlined,
  'person.fill': UserOutlined,
  'person.circle': UserOutlined,
  'person.crop.circle': UserOutlined,
  'person.crop.circle.fill': UserOutlined,
  'options': SettingOutlined,
  'library': BarsOutlined,
  'bookmarks': BookOutlined,
  'book.add': PlusOutlined,
  'book.fill': BookOutlined,
  'book-outline': BookOutlined,
  'book-sharp': BookOutlined,
  'close.circle': CloseCircleOutlined,
  'xmark.circle.fill': CloseCircleOutlined,
  'xmark': CloseCircleOutlined,
  'magnifyingglass': SearchOutlined,
  'search': SearchOutlined,
  'filter': FilterOutlined,
  'checkmark': CheckOutlined,
  'checkmark.square.fill': CheckSquareOutlined,
  'square': FileOutlined,
  'doc.text.fill': FileTextOutlined,
  'ellipsis-circle': EllipsisOutlined,
  'chatbubble': MessageOutlined,
  'file-tray': FileOutlined,
  'trash': DeleteOutlined,
  'share': ShareAltOutlined,
  'plus.circle.fill': PlusOutlined,
  'file.tray': FileOutlined,
  'sort-calendar-asc': CalendarOutlined,
  'sort-calendar-desc': CalendarOutlined,
  'sort-alpha-asc': CopyOutlined,
  'sort-alpha-desc': CopyOutlined,
  'sort-numeric-asc': CopyOutlined,
  'sort-numeric-desc': CopyOutlined,
  'moon': BellOutlined,
  'sun': HighlightOutlined,
  'arrow-expand-all': ZoomInOutlined,
  'comment-check': CheckOutlined,
  'comment-minus': DeleteOutlined,
  'comment-quote': MessageOutlined,
  'bell': BellOutlined,
  'pencil': FormOutlined,
  'exclamationmark.circle.fill': QuestionCircleOutlined,
  'globe': GlobalOutlined,
  'apple.logo': GlobalOutlined,
  'tag': TagOutlined,
  'tag.fill': TagOutlined,
  'doc.badge.gearshape': SettingOutlined,
  'questionmark.circle': QuestionCircleOutlined,
  'arrow.right.circle': ArrowRightOutlined,
  'note.text': FileTextOutlined,
  'gear': SettingOutlined,
  'calendar': CalendarOutlined,
  'folder': FolderOutlined,
  'google-analytics': HighlightOutlined,

  // Additional useful icons
  'audio': AudioOutlined,
  'document': FileWordOutlined,
  'highlight': HighlightOutlined
};

/**
 * IconSymbol component for web - renders Ant Design Icons
 * @param {string} name - Icon name (SF Symbol style)
 * @param {number} size - Icon size in pixels (default: 28)
 * @param {string} color - Icon color (CSS color value)
 * @param {object} style - Additional CSS styles
 * @param {string} className - CSS class name
 * @returns {JSX.Element}
 */
export function IconSymbol({
  name,
  size = 28,
  color = 'currentColor',
  style,
  className,
}) {
  const IconComponent = ICON_COMPONENTS[name];

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in MAPPING`);
    return null;
  }

  const baseStyle = {
    fontSize: `${size}px`,
    color,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style,
  };

  return (
    <IconComponent
      className={className}
      style={baseStyle}
      role="img"
      aria-label={name}
    />
  );
}

export default IconSymbol;
